import { useMemo } from 'react';
import Select from 'react-dropdown-select';
import './Dropdown.scss';
import { DropdownProps, Option } from './Dropdown.types';
import { normalizeTextInput } from '../general.utils';

export const Dropdown: React.FC<DropdownProps> = ({
  disabled = false,
  required = false,
  options = [],
  onChange,
  searchable = true,
  selectedOption,
  selectedMultiOptions,
  label,
  multi,
  create,
  onCreateNew,
  onChangeMultiple,
  autoFocus,
  onClose,
  clearable,
  defaultMenuIsOpen,
  smaller,
  minWidth,
}) => {
  /**
   * Sets the selected option or options on the first render
   * The <Select/> keeps track on the changed states itself
   */
  const selectedOptionFromOptions: Option[] = useMemo(
    (): Option[] =>
      multi
        ? options.filter((option) => selectedMultiOptions?.includes(option.value))
        : options.find((option) => option.value === selectedOption)
          ? [options.find((option) => option.value === selectedOption)!]
          : [],
    [multi, options, selectedMultiOptions, selectedOption],
  );

  /**
   * Helper method to handle the creation of an option instance
   * @param newOption the provided Option to create
   */
  const handleCreate = (newOption: Option): void => {
    if (!onCreateNew) return;
    const value: string = normalizeTextInput(newOption.value);
    const normalizedOption: Option = { label: value, value };
    onCreateNew(normalizedOption);
  };
  return (
    <div className="dropdown-component__wrapper" data-testid="dropdownComponent">
      {label && <p className="dropdown-component__label">{label}</p>}

      <Select
        backspaceDelete={false}
        style={{ minWidth: minWidth ?? '' }}
        clearable={clearable}
        data-testid="drop"
        required={required}
        disabled={disabled}
        values={selectedOptionFromOptions}
        autoFocus={autoFocus}
        onDropdownClose={onClose}
        defaultMenuIsOpen={defaultMenuIsOpen}
        options={options}
        dropdownPosition="auto"
        selectAll={multi}
        onChange={() => {}}
        onSelectAll={() =>
          onChangeMultiple && onChangeMultiple(options.flatMap((option) => option.value))
        }
        onClearAll={() => {
          if (onChangeMultiple) onChangeMultiple([]);
          else if (onChange) onChange('');
        }}
        onSelect={(selectedValues) => {
          const selectedOptions: Option[] = selectedValues as Option[];
          if (options.length === 0) return;
          if (onChangeMultiple) onChangeMultiple(selectedOptions.map((value) => value.value));
          else if (selectedOptions[0]?.value && selectedOptions.length === 1 && onChange)
            onChange(selectedOptions[0].value);
          else if (onChange) onChange('');
        }}
        onDeselect={(selectedValues) => {
          const selectedOptions: Option[] = selectedValues as Option[];

          if (options.length === 0) return;
          if (onChangeMultiple) onChangeMultiple(selectedOptions.map((value) => value.value));
          else if (selectedOptions[0]?.value && selectedOptions.length === 1 && onChange)
            onChange(selectedOptions[0].value);
          else if (onChange) onChange('');
        }}
        searchable={searchable}
        className={[
          'dropdown-component__select',
          searchable ? 'searchable' : 'non-searchable',
          smaller ? 'smaller' : '',
        ].join(' ')}
        dropdownGap={-5}
        multi={!!multi}
        onCreateNew={handleCreate}
        create={!!create}
      />
    </div>
  );
};
